import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { Breadcrumbs, PageTabs, PageTitle } from 'core/components';
import { IBreadcrumbsItem } from 'core/types';
import {
  AccessList,
  AdminUserList,
  CostingCalculationList,
  DocumentList,
  PartNumberList,
  RuleList,
  UpdatePricingProfileButton,
} from 'pricing/components';
import { PricingProfileDetailsTab, PricingProfilePricingType } from 'pricing/enums';
import { pricingProfileListPath } from 'pricing/routes';
import { IPricingProfile } from 'pricing/types';

import { PageSubtitle } from './PageSubtitle';

interface IProps {
  pricingProfile: IPricingProfile;
}

export const PricingProfileDetails = ({ pricingProfile }: IProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const tabs = [
    {
      value: PricingProfileDetailsTab.PART_NUMBER_BASED,
      label: t('labelPartNumberBased'),
      content: <PartNumberList pricingProfile={pricingProfile} />,
    },
    {
      value: PricingProfileDetailsTab.RULE_BASED,
      label: t('labelRuleBased'),
      content: <RuleList pricingProfile={pricingProfile} />,
    },
    {
      value: PricingProfileDetailsTab.COSTING_CALCULATION,
      label: t('labelCostingCalculation'),
      content: <CostingCalculationList pricingProfile={pricingProfile} />,
    },
    {
      value: PricingProfileDetailsTab.ACCESS,
      label: t('labelAccess'),
      content: <AccessList pricingProfile={pricingProfile} />,
    },
    {
      value: PricingProfileDetailsTab.DOCUMENTS,
      label: t('labelDocuments'),
      content: <DocumentList pricingProfile={pricingProfile} />,
    },
    {
      value: PricingProfileDetailsTab.ADMIN_USERS,
      label: t('labelAdminUsers'),
      content: <AdminUserList pricingProfile={pricingProfile} />,
    },
  ];

  const breadcrumbs: IBreadcrumbsItem[] = [
    {
      text: t('labelPricingProfiles'),
      onClick: () => navigate(pricingProfileListPath()),
    },
    {
      text: pricingProfile.name,
    },
  ];

  return (
    <>
      <Breadcrumbs breadcrumbs={breadcrumbs} />

      <PageTitle
        title={pricingProfile.name}
        subtitle={<PageSubtitle pricingProfile={pricingProfile} />}
        buttons={[
          <UpdatePricingProfileButton
            key="updatePricingProfileButton"
            type={PricingProfilePricingType.PRICING}
            pricingProfile={pricingProfile}
          />,
        ]}
      />

      <PageTabs
        tabs={tabs}
        defaultTab={PricingProfileDetailsTab.PART_NUMBER_BASED}
        ariaLabel="pricingProfileDetails"
      />
    </>
  );
};
